import m, {type Children, MithrilComponent, state} from '@bitstillery/mithril'

import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const profileCardMeta = {
    tier: 'content',
    summary: 'Person profile card (round photo, name, role, quote, mailto) — opens a detail dialog when given a `bio`.',
    when_to_use:
        'Use inside a `Grid` for meet-the-team, advisors or contact sections. Prefer a media-library `public_url` ' +
        'for `photo.src`. Give it a `bio` (and optionally `phone` / `detail_photo`) and the portrait becomes a ' +
        'button that opens the person’s detail dialog — the card itself keeps showing the short `quote`. ' +
        'For a “join us”/empty slot at the end of the grid, use a `PlaceholderCard` instead.',
    example: `
    <ProfileCard
        email="cor@example.com"
        name="Cor"
        photo={{alt: 'Cor', src: '/common/img/catalog/cor.jpg'}}
        quote="I know every supplier on my list by name."
        role="Account manager"
    />
    `,
    variants: [
        {
            prop: 'bio',
            jsx: `
            <ProfileCard
                bio="Cor has been sourcing spirits for fifteen years."
                email="cor@example.com"
                name="Cor"
                phone="+31 20 123 4567"
                photo={{alt: 'Cor', src: '/common/img/catalog/cor.jpg'}}
                quote="I know every supplier on my list by name."
                role="Account manager"
            />
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface ProfileCardAttrs {
    /** Full personal story, shown in the detail dialog; the card keeps showing the short `quote`. Omit for a card without a dialog. */
    bio?: Children
    /** Photo of what this person does outside work, shown at the bottom of the detail dialog. */
    detail_photo?: {alt: string; src: string}
    /** Contact address for the mailto link. */
    email: string
    /** Person’s display name. */
    name: string
    /** Contact phone number, shown in the detail dialog. */
    phone?: string
    /** Portrait image; prefer a media-library public_url for src. */
    photo: {alt: string; src: string}
    /** Short testimonial; typographic quotes are added by the component. */
    quote: Children
    /** Job title or role line. */
    role: string
}

/**
 * Portrait + quote card for a person. Intended inside a {@link Grid} (meet-the-team, advisors, contact).
 *
 * When a `bio` is passed the portrait turns into a button that opens a detail dialog with the full
 * story, contact details and — when there is one — a photo of what the person does outside work.
 */
export class ProfileCard extends MithrilComponent<ProfileCardAttrs> {
    data = state({open: false}, 'cms-profile-card')

    view(vnode: m.Vnode<ProfileCardAttrs>) {
        const {bio, detail_photo, email, name, phone, photo, quote, role} = vnode.attrs
        const has_detail = Boolean(bio)

        return (
            <article className='view-profile-card'>
                <div className='view-profile-card-inner'>
                    {has_detail ? (
                        <button
                            aria-haspopup='dialog'
                            aria-label={`More about ${name}`}
                            className='view-profile-card-photo view-profile-card-photo-button'
                            onclick={() => {
                                this.data.open = true
                            }}
                            type='button'
                        >
                            <img alt={photo.alt} src={photo.src} />
                            <span aria-hidden='true' className='view-profile-card-photo-badge'>
                                <Icon name='plus' size='s' type='unset' />
                            </span>
                        </button>
                    ) : (
                        <div className='view-profile-card-photo'>
                            <img alt={photo.alt} src={photo.src} />
                        </div>
                    )}
                    <div className='view-profile-card-content'>
                        <h3 className='view-profile-card-name'>{name}</h3>
                        <p className='view-profile-card-role'>{role}</p>
                        <blockquote className='view-profile-card-quote'>
                            {'“'}
                            {quote}
                            {'”'}
                        </blockquote>
                        <a className='view-profile-card-email' href={`mailto:${email}`}>
                            {email}
                        </a>
                    </div>
                </div>
                {has_detail && this.data.open ? (
                    // A native modal <dialog>: this card renders inside the CMS content surface, which is
                    // its own stacking context, so a fixed overlay here paints *under* the hero band and
                    // header strip whatever its z-index. showModal() lifts it into the top layer instead,
                    // and brings the backdrop, focus trap and Escape handling with it.
                    <dialog
                        aria-label={name}
                        className='view-profile-detail-dialog'
                        oncancel={() => {
                            this.data.open = false
                        }}
                        onclick={(event: MouseEvent) => {
                            // A click on the backdrop lands on the dialog box itself; the panel is a child.
                            if (event.target === event.currentTarget) {
                                this.data.open = false
                            }
                        }}
                        oncreate={(dialog: m.VnodeDOM) => {
                            if (dialog.dom instanceof HTMLDialogElement) dialog.dom.showModal()
                        }}
                    >
                        {/* Autofocus the panel, not the close button: showModal() otherwise lands on the
                            first focusable child and rings it. */}
                        <div autofocus className='view-profile-detail' tabindex={-1}>
                            <button
                                aria-label='Close'
                                className='view-profile-detail-close'
                                onclick={() => {
                                    this.data.open = false
                                }}
                                type='button'
                            >
                                <Icon name='close' size='l' type='unset' />
                            </button>
                            <div className='view-profile-detail-intro'>
                                <img alt={photo.alt} className='view-profile-detail-portrait' src={photo.src} />
                                <div>
                                    <h2 className='view-profile-detail-name'>{name}</h2>
                                    <p className='view-profile-detail-role'>{role}</p>
                                </div>
                            </div>
                            <div className='view-profile-detail-body'>
                                <div className='view-profile-detail-bio'>{bio}</div>
                                <div className='view-profile-detail-contact'>
                                    {phone ? (
                                        <a href={`tel:${phone.replace(/\s/g, '')}`}>
                                            <Icon name='phone' size='s' type='unset' />
                                            {phone}
                                        </a>
                                    ) : null}
                                    <a href={`mailto:${email}`}>
                                        <Icon name='email' size='s' type='unset' />
                                        {email}
                                    </a>
                                </div>
                            </div>
                            {detail_photo ? (
                                <div className='view-profile-detail-photo'>
                                    <img alt={detail_photo.alt} decoding='async' loading='lazy' src={detail_photo.src} />
                                </div>
                            ) : null}
                        </div>
                    </dialog>
                ) : null}
            </article>
        )
    }
}
